import {useState} from 'react'
import {IntlShape, useIntl} from 'react-intl'
import {PROFILE_MESSAGES as M} from '../profileMessages'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {CREATE_AGENT_REFERRAL_INVITATION} from '../../../gql/mutations/agentOnboardingMutations'

interface IModalInviteReferredProps {
  isOpen: boolean
  onClose: () => void
}

/** Fabrica: el esquema necesita `intl`, asi que no puede vivir a nivel de modulo. */
const buildValidationSchema = (intl: IntlShape) =>
  Yup.object({
    email: Yup.string()
      .email(intl.formatMessage(M.vEmailInvalid))
      .required(intl.formatMessage(M.vEmailRequired)),
  })

export const ModalInviteReferred: React.FC<IModalInviteReferredProps> = ({isOpen, onClose}) => {
  const intl = useIntl()
  const [createInvitation, {loading}] = useMutation(CREATE_AGENT_REFERRAL_INVITATION)
  const [fallbackLink, setFallbackLink] = useState<string | null>(null)

  const handleClose = () => {
    setFallbackLink(null)
    formik.resetForm()
    onClose()
  }

  const formik = useFormik({
    initialValues: {email: ''},
    validationSchema: buildValidationSchema(intl),
    onSubmit: async (values, {resetForm}) => {
      try {
        const {data} = await createInvitation({variables: {email: values.email}})
        const result = data?.createAgentReferralInvitation
        if (result?.success) {
          if (result.emailSent) {
            toast.success(intl.formatMessage(M.toastInvitationSent, {email: values.email}))
            resetForm()
            onClose()
          } else {
            toast.warning(intl.formatMessage(M.inviteEmailFailed))
            setFallbackLink(result.inviteUrl)
          }
        } else {
          toast.error(result?.errors?.[0]?.message || intl.formatMessage(M.errInviteFailed))
        }
      } catch (err: any) {
        toast.error(err?.message || intl.formatMessage(M.errInviteFailed))
      }
    },
  })

  return (
    <Modal show={isOpen} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.inviteTitle)}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Body>
          <Form.Group>
            <Form.Label>{intl.formatMessage(M.email)}</Form.Label>
            <Form.Control
              type='email'
              name='email'
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={!!formik.touched.email && !!formik.errors.email}
            />
            <Form.Control.Feedback type='invalid'>{formik.errors.email}</Form.Control.Feedback>
          </Form.Group>
          {fallbackLink && (
            <Form.Group className='mt-3'>
              <Form.Label>{intl.formatMessage(M.inviteLink)}</Form.Label>
              <Form.Control readOnly value={fallbackLink} onFocus={(e) => e.target.select()} />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleClose} disabled={loading}>
            {intl.formatMessage(M.cancel)}
          </Button>
          <Button variant='primary' type='submit' disabled={loading}>
            {loading ? <Spinner animation='border' size='sm' /> : intl.formatMessage(M.sendInvitation)}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}
